/**
 * Servicios para catálogos del sistema
 * Especialidades, Clases, Cargos, Conceptos Financieros
 */

import { where, orderBy } from "firebase/firestore";
import { getById, getAll, create, update } from "./base";

// ==================== TIPOS ====================

import type { ClubType, Specialty, ClassCatalog, Position } from "@/types";

// Re-export from types for backwards compatibility
export type { ClubType, Specialty, Position } from "@/types";
export type ProgressClass = ClassCatalog;

export interface FinancialConcept {
  id: string;
  name: string;
  code: string;
  type: "income" | "expense";
  category: "membership" | "event" | "monthly" | "other";
  defaultAmount?: number;
  isRecurring: boolean;
  description?: string;
  status: "active" | "inactive";
  createdAt?: Date;
  updatedAt?: Date;
}

export interface AcademicPeriod {
  id: string;
  name: string;
  year: number;
  startDate: Date;
  endDate: Date;
  isActive: boolean;
  status: "active" | "inactive";
  createdAt?: Date;
  updatedAt?: Date;
}

// ==================== SPECIALTIES ====================

export const specialtyService = {
  async getById(id: string): Promise<Specialty | null> {
    return getById<Specialty>("specialties", id);
  },

  async getAll(): Promise<Specialty[]> {
    return getAll<Specialty>("specialties", [
      where("status", "==", "active"),
      orderBy("name"),
    ]);
  },

  async getByClubType(clubType: ClubType): Promise<Specialty[]> {
    return getAll<Specialty>("specialties", [
      where("clubTypes", "array-contains", clubType),
      where("status", "==", "active"),
      orderBy("name"),
    ]);
  },

  async getByCategory(category: string): Promise<Specialty[]> {
    return getAll<Specialty>("specialties", [
      where("category", "==", category),
      where("status", "==", "active"),
      orderBy("name"),
    ]);
  },

  async create(
    data: Omit<Specialty, "id" | "createdAt" | "updatedAt">,
    userId: string,
  ): Promise<string> {
    return create<Specialty>("specialties", data, userId);
  },

  async update(id: string, data: Partial<Specialty>): Promise<void> {
    return update<Specialty>("specialties", id, data);
  },

  async delete(id: string): Promise<void> {
    // Soft delete
    return update<Specialty>("specialties", id, { status: "inactive" });
  },

  async getCategories(): Promise<string[]> {
    const specialties = await getAll<Specialty>("specialties", [
      where("status", "==", "active"),
    ]);
    const categories = [...new Set(specialties.map((s) => s.category))];
    return categories.sort();
  },
};

// ==================== MAESTRIAS ====================

import type { Maestria, Certificacion } from "@/types";

export const maestriaService = {
  async getById(id: string): Promise<Maestria | null> {
    return getById<Maestria>("maestrias", id);
  },

  async getAll(): Promise<Maestria[]> {
    return getAll<Maestria>("maestrias", [
      where("status", "==", "active"),
      orderBy("name"),
    ]);
  },

  async getByClubType(clubType: ClubType): Promise<Maestria[]> {
    return getAll<Maestria>("maestrias", [
      where("clubTypes", "array-contains", clubType),
      where("status", "==", "active"),
      orderBy("name"),
    ]);
  },

  async create(
    data: Omit<Maestria, "id" | "createdAt" | "updatedAt">,
    userId: string,
  ): Promise<string> {
    return create<Maestria>("maestrias", data, userId);
  },

  async update(id: string, data: Partial<Maestria>): Promise<void> {
    return update<Maestria>("maestrias", id, data);
  },

  async delete(id: string): Promise<void> {
    return update<Maestria>("maestrias", id, { status: "inactive" });
  },
};

// ==================== CERTIFICACIONES ====================

export const certificacionService = {
  async getById(id: string): Promise<Certificacion | null> {
    return getById<Certificacion>("certificaciones", id);
  },

  async getAll(): Promise<Certificacion[]> {
    const results = await getAll<Certificacion>("certificaciones", [
      where("status", "==", "active"),
    ]);
    return results.sort((a, b) => a.name.localeCompare(b.name));
  },

  async getByClubType(clubType: ClubType): Promise<Certificacion[]> {
    const results = await getAll<Certificacion>("certificaciones", [
      where("clubTypes", "array-contains", clubType),
      where("status", "==", "active"),
    ]);
    return results.sort((a, b) => a.name.localeCompare(b.name));
  },

  async create(
    data: Omit<Certificacion, "id" | "createdAt" | "updatedAt">,
    userId: string,
  ): Promise<string> {
    return create<Certificacion>("certificaciones", data, userId);
  },

  async update(id: string, data: Partial<Certificacion>): Promise<void> {
    return update<Certificacion>("certificaciones", id, data);
  },

  async delete(id: string): Promise<void> {
    return update<Certificacion>("certificaciones", id, { status: "inactive" });
  },
};

// ==================== PROGRESS CLASSES ====================

export const progressClassService = {
  async getById(id: string): Promise<ProgressClass | null> {
    return getById<ProgressClass>("progressClasses", id);
  },

  async getAll(): Promise<ProgressClass[]> {
    return getAll<ProgressClass>("progressClasses", [
      where("status", "==", "active"),
      orderBy("order"),
    ]);
  },

  async getByClubType(clubType: ClubType): Promise<ProgressClass[]> {
    return getAll<ProgressClass>("progressClasses", [
      where("clubType", "==", clubType),
      where("status", "==", "active"),
      orderBy("order"),
    ]);
  },

  async create(
    data: Omit<ProgressClass, "id" | "createdAt" | "updatedAt">,
    userId: string,
  ): Promise<string> {
    return create<ProgressClass>("progressClasses", data, userId);
  },

  async update(id: string, data: Partial<ProgressClass>): Promise<void> {
    return update<ProgressClass>("progressClasses", id, data);
  },

  async delete(id: string): Promise<void> {
    return update<ProgressClass>("progressClasses", id, { status: "inactive" });
  },
};

// ==================== POSITIONS ====================

export const positionService = {
  async getById(id: string): Promise<Position | null> {
    return getById<Position>("positions", id);
  },

  async getAll(): Promise<Position[]> {
    return getAll<Position>("positions", [
      where("status", "==", "active"),
      orderBy("name"),
    ]);
  },

  async getByLevel(level: Position["level"]): Promise<Position[]> {
    return getAll<Position>("positions", [
      where("level", "==", level),
      where("status", "==", "active"),
      orderBy("name"),
    ]);
  },

  async create(
    data: Omit<Position, "id" | "createdAt" | "updatedAt">,
    userId: string,
  ): Promise<string> {
    return create<Position>("positions", data, userId);
  },

  async update(id: string, data: Partial<Position>): Promise<void> {
    return update<Position>("positions", id, data);
  },

  async delete(id: string): Promise<void> {
    return update<Position>("positions", id, { status: "inactive" });
  },
};

// ==================== FINANCIAL CONCEPTS ====================

export const financialConceptService = {
  async getById(id: string): Promise<FinancialConcept | null> {
    return getById<FinancialConcept>("financialConcepts", id);
  },

  async getAll(): Promise<FinancialConcept[]> {
    return getAll<FinancialConcept>("financialConcepts", [
      where("status", "==", "active"),
      orderBy("name"),
    ]);
  },

  async getByType(type: "income" | "expense"): Promise<FinancialConcept[]> {
    return getAll<FinancialConcept>("financialConcepts", [
      where("type", "==", type),
      where("status", "==", "active"),
      orderBy("name"),
    ]);
  },

  async getByCategory(
    category: FinancialConcept["category"],
  ): Promise<FinancialConcept[]> {
    return getAll<FinancialConcept>("financialConcepts", [
      where("category", "==", category),
      where("status", "==", "active"),
      orderBy("name"),
    ]);
  },

  async create(
    data: Omit<FinancialConcept, "id" | "createdAt" | "updatedAt">,
    userId: string,
  ): Promise<string> {
    return create<FinancialConcept>("financialConcepts", data, userId);
  },

  async update(id: string, data: Partial<FinancialConcept>): Promise<void> {
    return update<FinancialConcept>("financialConcepts", id, data);
  },

  async delete(id: string): Promise<void> {
    return update<FinancialConcept>("financialConcepts", id, {
      status: "inactive",
    });
  },
};

// ==================== ACADEMIC PERIODS ====================

export const academicPeriodService = {
  async getById(id: string): Promise<AcademicPeriod | null> {
    return getById<AcademicPeriod>("academicPeriods", id);
  },

  async getAll(): Promise<AcademicPeriod[]> {
    return getAll<AcademicPeriod>("academicPeriods", [orderBy("year", "desc")]);
  },

  async getActive(): Promise<AcademicPeriod | null> {
    const periods = await getAll<AcademicPeriod>("academicPeriods", [
      where("isActive", "==", true),
    ]);
    return periods[0] || null;
  },

  async create(
    data: Omit<AcademicPeriod, "id" | "createdAt" | "updatedAt">,
    userId: string,
  ): Promise<string> {
    // Si es activo, desactivar los demás
    if (data.isActive) {
      const activePeriods = await this.getAll();
      for (const period of activePeriods.filter((p) => p.isActive)) {
        await update<AcademicPeriod>("academicPeriods", period.id, {
          isActive: false,
        });
      }
    }
    return create<AcademicPeriod>("academicPeriods", data, userId);
  },

  async update(id: string, data: Partial<AcademicPeriod>): Promise<void> {
    // Si se activa, desactivar los demás
    if (data.isActive) {
      const activePeriods = await this.getAll();
      for (const period of activePeriods.filter(
        (p) => p.isActive && p.id !== id,
      )) {
        await update<AcademicPeriod>("academicPeriods", period.id, {
          isActive: false,
        });
      }
    }
    return update<AcademicPeriod>("academicPeriods", id, data);
  },

  async setActive(id: string): Promise<void> {
    return this.update(id, { isActive: true });
  },

  async delete(id: string): Promise<void> {
    return update<AcademicPeriod>("academicPeriods", id, {
      status: "inactive",
    });
  },
};

// ==================== SEED DATA ====================

export async function seedCatalogs(userId: string): Promise<void> {
  // Verificar si ya hay datos
  const existingSpecialties = await specialtyService.getAll();
  if (existingSpecialties.length > 0) {
    console.log("Catálogos ya existen, omitiendo seed");
    return;
  }

  console.log("Creando catálogos iniciales...");

  // Especialidades de ejemplo
  const specialties: Omit<Specialty, "id" | "createdAt" | "updatedAt">[] = [
    // Aventureros
    {
      name: "Amigo de Jesús",
      code: "AJ",
      category: "Espiritual",
      clubTypes: ["aventureros"],
      status: "active",
    },
    {
      name: "Amigo de la Naturaleza",
      code: "AN",
      category: "Naturaleza",
      clubTypes: ["aventureros"],
      status: "active",
    },
    {
      name: "Amigo del Hogar",
      code: "AH",
      category: "Hogar",
      clubTypes: ["aventureros"],
      status: "active",
    },
    // Conquistadores
    {
      name: "Arte Culinario",
      code: "AC",
      category: "Artes Domésticas",
      clubTypes: ["conquistadores"],
      status: "active",
    },
    {
      name: "Anfibios",
      code: "ANF",
      category: "Naturaleza",
      clubTypes: ["conquistadores"],
      status: "active",
    },
    {
      name: "Aves",
      code: "AV",
      category: "Naturaleza",
      clubTypes: ["conquistadores"],
      status: "active",
    },
    {
      name: "Arte de Hacer Nudos",
      code: "NU",
      category: "Campamento",
      clubTypes: ["conquistadores"],
      status: "active",
    },
    {
      name: "Fogatas y Cocina al Aire Libre",
      code: "FC",
      category: "Campamento",
      clubTypes: ["conquistadores"],
      status: "active",
    },
    // Guías Mayores
    {
      name: "Liderazgo",
      code: "LID",
      category: "Desarrollo",
      clubTypes: ["guias_mayores"],
      status: "active",
    },
    {
      name: "Instrucción",
      code: "INS",
      category: "Desarrollo",
      clubTypes: ["guias_mayores"],
      status: "active",
    },
    {
      name: "Evangelismo Juvenil",
      code: "EJ",
      category: "Espiritual",
      clubTypes: ["guias_mayores"],
      status: "active",
    },
  ];

  for (const specialty of specialties) {
    await specialtyService.create(specialty, userId);
  }

  // Clases progresivas
  const progressClasses: Omit<
    ProgressClass,
    "id" | "createdAt" | "updatedAt"
  >[] = [
    // Aventureros
    {
      name: "Abejita Industriosa",
      code: "ABE",
      clubType: "aventureros",
      order: 1,
      minAge: 6,
      maxAge: 6,
      status: "active",
    },
    {
      name: "Rayito de Sol",
      code: "RAY",
      clubType: "aventureros",
      order: 2,
      minAge: 7,
      maxAge: 7,
      status: "active",
    },
    {
      name: "Constructor",
      code: "CON",
      clubType: "aventureros",
      order: 3,
      minAge: 8,
      maxAge: 8,
      status: "active",
    },
    {
      name: "Manos Ayudadoras",
      code: "MAN",
      clubType: "aventureros",
      order: 4,
      minAge: 9,
      maxAge: 9,
      status: "active",
    },
    // Conquistadores
    {
      name: "Amigo",
      code: "AMI",
      clubType: "conquistadores",
      order: 1,
      minAge: 10,
      maxAge: 10,
      status: "active",
    },
    {
      name: "Compañero",
      code: "COM",
      clubType: "conquistadores",
      order: 2,
      minAge: 11,
      maxAge: 11,
      status: "active",
    },
    {
      name: "Explorador",
      code: "EXP",
      clubType: "conquistadores",
      order: 3,
      minAge: 12,
      maxAge: 12,
      status: "active",
    },
    {
      name: "Orientador",
      code: "ORI",
      clubType: "conquistadores",
      order: 4,
      minAge: 13,
      maxAge: 13,
      status: "active",
    },
    {
      name: "Viajero",
      code: "VIA",
      clubType: "conquistadores",
      order: 5,
      minAge: 14,
      maxAge: 14,
      status: "active",
    },
    {
      name: "Guía",
      code: "GUI",
      clubType: "conquistadores",
      order: 6,
      minAge: 15,
      maxAge: 15,
      status: "active",
    },
    // Guías Mayores
    {
      name: "Guía Mayor",
      code: "GM",
      clubType: "guias_mayores",
      order: 1,
      minAge: 16,
      status: "active",
    },
    {
      name: "Guía Mayor Master",
      code: "GMM",
      clubType: "guias_mayores",
      order: 2,
      minAge: 18,
      status: "active",
    },
    {
      name: "Guía Mayor Master Avanzado",
      code: "GMMA",
      clubType: "guias_mayores",
      order: 3,
      minAge: 20,
      status: "active",
    },
  ];

  for (const pc of progressClasses) {
    await progressClassService.create(pc, userId);
  }

  // Cargos
  const positions: Omit<Position, "id" | "createdAt" | "updatedAt">[] = [
    // Club
    { name: "Director(a)", code: "DIR", level: "club", status: "active" },
    { name: "Subdirector(a)", code: "SDIR", level: "club", status: "active" },
    { name: "Secretario(a)", code: "SEC", level: "club", status: "active" },
    { name: "Tesorero(a)", code: "TES", level: "club", status: "active" },
    { name: "Consejero(a)", code: "CONS", level: "unit", status: "active" },
    { name: "Instructor(a)", code: "INST", level: "club", status: "active" },
    {
      name: "Capitán",
      code: "CAP",
      level: "unit",
      clubTypes: ["aventureros"],
      status: "active",
    },
    {
      name: "Capitán de Patrulla",
      code: "CAPP",
      level: "unit",
      clubTypes: ["conquistadores"],
      status: "active",
    },
    // Iglesia
    {
      name: "Coordinador de Clubes",
      code: "COORD",
      level: "church",
      status: "active",
    },
    // Distrito
    {
      name: "Director de Distrito",
      code: "DDIST",
      level: "district",
      status: "active",
    },
    // Asociación
    {
      name: "Director de Asociación",
      code: "DASOC",
      level: "association",
      status: "active",
    },
    // Unión
    {
      name: "Director de Unión",
      code: "DUNIO",
      level: "union",
      status: "active",
    },
  ];

  for (const position of positions) {
    await positionService.create(position, userId);
  }

  // Conceptos financieros
  const financialConcepts: Omit<
    FinancialConcept,
    "id" | "createdAt" | "updatedAt"
  >[] = [
    {
      name: "Anualidad",
      code: "ANUAL",
      type: "income",
      category: "membership",
      defaultAmount: 50000,
      isRecurring: false,
      status: "active",
    },
    {
      name: "Cuota Mensual",
      code: "MENSUAL",
      type: "income",
      category: "monthly",
      defaultAmount: 20000,
      isRecurring: true,
      status: "active",
    },
    {
      name: "Inscripción Evento",
      code: "INS-EV",
      type: "income",
      category: "event",
      isRecurring: false,
      status: "active",
    },
    {
      name: "Uniforme",
      code: "UNIF",
      type: "income",
      category: "other",
      isRecurring: false,
      status: "active",
    },
    {
      name: "Materiales",
      code: "MAT",
      type: "expense",
      category: "other",
      isRecurring: false,
      status: "active",
    },
    {
      name: "Transporte",
      code: "TRANS",
      type: "expense",
      category: "event",
      isRecurring: false,
      status: "active",
    },
    {
      name: "Alimentación",
      code: "ALIM",
      type: "expense",
      category: "event",
      isRecurring: false,
      status: "active",
    },
  ];

  for (const concept of financialConcepts) {
    await financialConceptService.create(concept, userId);
  }

  // Periodo académico actual
  const currentYear = new Date().getFullYear();
  await academicPeriodService.create(
    {
      name: `Periodo ${currentYear}`,
      year: currentYear,
      startDate: new Date(currentYear, 1, 1), // Febrero
      endDate: new Date(currentYear, 10, 30), // Noviembre
      isActive: true,
      status: "active",
    },
    userId,
  );

  console.log("Catálogos creados exitosamente");
}
